import { useEffect, useState } from 'react';
import { AlertCircle, ArrowDown, ArrowUp, CheckCircle, Loader2, Plus, Save, Trash2, User, X } from 'lucide-react';
import { labApi } from '@/lib/api';
import { AvatarPicker, type ProductionAvatarOption } from './ProductionPickers';

// Edytowalna tabela strukturalnych scen skryptu produkcyjnego (metadata.scenes).
// Edycja inline per pole + dodawanie/usuwanie/reorder; zapis regeneruje markdown
// po stronie backendu. Produkcja bierze spoken_text wprost z tych danych.
// EDYTOR UJĘĆ (profil HeyGen): każda scena może mieć przypisany look avatara
// z biblioteki — produkcja generuje osobny klip per scena z tym lookiem.

export interface VideoSceneRecord {
  scene_id: number;
  section: string;
  duration: string;
  spoken_text: string;
  screen_text: string;
  visual: string;
  delivery: string;
  gestures: string;
  look_id?: string;
  look_name?: string;
}

interface LibraryLookOption {
  lookId: string;
  name: string;
  previewUrl: string | null;
}

interface SceneTableEditorProps {
  productId: string;
  assetId: string;
  scenes: VideoSceneRecord[];
  showGestures: boolean; // heygen = tak, elevenlabs = nie
  onSaved: () => void;
}

const EMPTY_SCENE: Omit<VideoSceneRecord, 'scene_id'> = {
  section: '',
  duration: '',
  spoken_text: '',
  screen_text: '',
  visual: '',
  delivery: '',
  gestures: '',
};

export function SceneTableEditor({ productId, assetId, scenes: initialScenes, showGestures, onSaved }: SceneTableEditorProps) {
  const [scenes, setScenes] = useState<VideoSceneRecord[]>(initialScenes);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [libraryLooks, setLibraryLooks] = useState<LibraryLookOption[]>([]);
  const [lookPickerIndex, setLookPickerIndex] = useState<number | null>(null);

  // Looki z biblioteki avatarów do przypisania per scena (tylko profil HeyGen)
  useEffect(() => {
    if (!showGestures) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await labApi.getAvatars();
        if (cancelled) return;
        const records = (res.data.data || []) as Array<{
          name: string; status: string; heygenAvatarId: string | null; previewUrl: string | null;
          scenes: Array<{ name: string; status: string; heygenLookId: string | null; previewUrl: string | null }>;
        }>;
        setLibraryLooks(records.flatMap(avatar => {
          if (avatar.status !== 'READY' || !avatar.heygenAvatarId || !avatar.previewUrl) return [];
          return [
            { lookId: avatar.heygenAvatarId, name: avatar.name, previewUrl: avatar.previewUrl },
            ...avatar.scenes
              .filter(scene => scene.status === 'READY' && scene.heygenLookId && scene.previewUrl)
              .map(scene => ({ lookId: scene.heygenLookId as string, name: `${avatar.name} - ${scene.name}`, previewUrl: scene.previewUrl })),
          ];
        }));
      } catch (err) {
        console.error('Failed to load library looks:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [showGestures]);

  const mutate = (next: VideoSceneRecord[]) => {
    setScenes(next.map((scene, index) => ({ ...scene, scene_id: index + 1 })));
    setDirty(true);
    setSaveStatus('idle');
  };

  const updateField = (index: number, field: keyof VideoSceneRecord, value: string) => {
    mutate(scenes.map((scene, i) => (i === index ? { ...scene, [field]: value } : scene)));
  };

  const move = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= scenes.length) return;
    const next = [...scenes];
    [next[index], next[target]] = [next[target], next[index]];
    mutate(next);
  };

  const remove = (index: number) => {
    mutate(scenes.filter((_, i) => i !== index));
  };

  const addScene = (afterIndex?: number) => {
    const next = [...scenes];
    const insertAt = afterIndex != null ? afterIndex + 1 : next.length;
    next.splice(insertAt, 0, { ...EMPTY_SCENE, scene_id: 0 });
    mutate(next);
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      await labApi.updateVideoAssetScenes(productId, assetId, scenes as unknown as Array<Record<string, unknown>>);
      setDirty(false);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nie udało się zapisać scen.');
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const smallInput = (index: number, field: keyof VideoSceneRecord, placeholder: string, className = '') => (
    <input
      type="text"
      value={String(scenes[index][field] ?? '')}
      onChange={e => updateField(index, field, e.target.value)}
      placeholder={placeholder}
      className={`text-[11px] bg-surface-hover border border-border rounded-md px-2 py-1 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40 ${className}`}
    />
  );

  return (
    <div className="space-y-2">
      {showGestures && (
        <p className="text-[11px] text-text-muted">
          Każda scena to osobny klip HeyGen z przypisanym ujęciem. Klipy trafiają na Drive w częściach - montaż i przycinanie w Studio.
        </p>
      )}
      {scenes.map((scene, index) => (
        <div key={index} className="rounded-lg border border-border bg-surface-hover/30 p-2.5 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-accent-info shrink-0">SCENA {index + 1}</span>
            {smallInput(index, 'section', 'Sekcja (HOOK, CTA...)', 'w-32')}
            {smallInput(index, 'duration', 'Timing (0-3s)', 'w-24')}
            <div className="ml-auto flex items-center gap-1 shrink-0">
              <button
                onClick={() => move(index, -1)}
                disabled={index === 0}
                className="p-1 text-text-muted hover:text-text-primary transition-colors disabled:opacity-30"
                title="Przesuń w górę"
                aria-label="Przesuń scenę w górę"
              >
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => move(index, 1)}
                disabled={index === scenes.length - 1}
                className="p-1 text-text-muted hover:text-text-primary transition-colors disabled:opacity-30"
                title="Przesuń w dół"
                aria-label="Przesuń scenę w dół"
              >
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => addScene(index)}
                className="p-1 text-text-muted hover:text-accent-info transition-colors"
                title="Dodaj scenę poniżej"
                aria-label="Dodaj scenę poniżej"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => remove(index)}
                disabled={scenes.length <= 1}
                className="p-1 text-text-muted hover:text-red-400 transition-colors disabled:opacity-30"
                title="Usuń scenę"
                aria-label="Usuń scenę"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-medium text-text-muted block mb-0.5">Tekst wypowiedzi (idzie do produkcji)</label>
            <textarea
              value={scene.spoken_text}
              onChange={e => updateField(index, 'spoken_text', e.target.value)}
              rows={2}
              className="w-full text-[11px] bg-surface-hover border border-border rounded-md px-2 py-1.5 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40"
              placeholder="Dokładny tekst tej sceny..."
            />
          </div>

          {showGestures && (
            <div>
              <span className="text-[10px] font-medium text-text-muted block mb-0.5">
                Ujęcie (avatar dla tej sceny)
              </span>
              <button
                type="button"
                onClick={() => setLookPickerIndex(index)}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border transition-all text-left w-full sm:w-auto sm:min-w-[220px] ${
                  scene.look_id ? 'border-accent-primary/30 bg-accent-primary/5' : 'border-border bg-surface-hover/40 hover:border-border-strong'
                }`}
              >
                <span className="w-7 h-7 rounded-md overflow-hidden bg-surface-hover border border-border-subtle shrink-0">
                  {(() => {
                    const look = libraryLooks.find(item => item.lookId === scene.look_id);
                    return look?.previewUrl
                      ? <img src={look.previewUrl} alt={look.name} className="w-full h-full object-cover" />
                      : <span className="w-full h-full flex items-center justify-center"><User className="w-3.5 h-3.5 text-text-disabled" /></span>;
                  })()}
                </span>
                <span className="min-w-0">
                  <span className={`text-[11px] font-medium block truncate ${scene.look_id ? 'text-accent-primary' : 'text-text-secondary'}`}>
                    {scene.look_name || 'Wybierz ujęcie'}
                  </span>
                  <span className="text-[9px] text-text-muted block">kliknij, żeby wybrać z galerii</span>
                </span>
                {scene.look_id && (
                  <X
                    className="w-3.5 h-3.5 text-text-muted hover:text-red-400 ml-auto shrink-0"
                    onClick={e => {
                      e.stopPropagation();
                      mutate(scenes.map((item, i) => (i === index ? { ...item, look_id: '', look_name: '' } : item)));
                    }}
                  />
                )}
              </button>
            </div>
          )}

          <div className={`grid gap-2 ${showGestures ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 lg:grid-cols-3'}`}>
            <div>
              <label className="text-[10px] font-medium text-text-muted block mb-0.5">Tekst na ekran</label>
              {smallInput(index, 'screen_text', 'Overlay...', 'w-full')}
            </div>
            <div>
              <label className="text-[10px] font-medium text-text-muted block mb-0.5">Wizual</label>
              {smallInput(index, 'visual', 'Co widać...', 'w-full')}
            </div>
            <div>
              <label className="text-[10px] font-medium text-text-muted block mb-0.5">Emocja / delivery</label>
              {smallInput(index, 'delivery', 'Tempo, pauzy, emocja...', 'w-full')}
            </div>
            {showGestures && (
              <div>
                <label className="text-[10px] font-medium text-text-muted block mb-0.5">Gesty avatara</label>
                {smallInput(index, 'gestures', 'np. pokazuje produkt...', 'w-full')}
              </div>
            )}
          </div>
        </div>
      ))}

      {error && (
        <div className="flex items-center gap-2 text-[11px] text-red-400">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Zapisz sceny
        </button>
        <button
          onClick={() => addScene()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-accent-info bg-info/10 border border-accent-info/20 rounded-lg hover:bg-info/15 transition-all"
        >
          <Plus className="w-3.5 h-3.5" /> Dodaj scenę
        </button>
        {saveStatus === 'success' && (
          <span className="flex items-center gap-1.5 text-[11px] text-success animate-fade-in">
            <CheckCircle className="w-3.5 h-3.5" /> Zapisano
          </span>
        )}
      </div>

      {lookPickerIndex != null && (
        <LookPickerModal
          looks={libraryLooks}
          value={scenes[lookPickerIndex]?.look_id || ''}
          onSelect={lookId => {
            const lookName = libraryLooks.find(look => look.lookId === lookId)?.name || '';
            mutate(scenes.map((item, i) => (i === lookPickerIndex ? { ...item, look_id: lookId, look_name: lookName } : item)));
            setLookPickerIndex(null);
          }}
          onClose={() => setLookPickerIndex(null)}
        />
      )}
    </div>
  );
}

// Galeria ujęć (avatary + sceny z kreatora) do przypisania scenie skryptu
function LookPickerModal({ looks, value, onSelect, onClose }: {
  looks: LibraryLookOption[];
  value: string;
  onSelect: (lookId: string) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const options: ProductionAvatarOption[] = looks.map(look => ({
    avatarId: look.lookId,
    name: look.name,
    previewUrl: look.previewUrl,
    library: true,
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-raised rounded-xl border border-border w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-text-primary">Wybierz ujęcie dla sceny</h3>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors" aria-label="Zamknij">
            <X className="w-4 h-4" />
          </button>
        </div>
        {looks.length === 0 ? (
          <p className="text-xs text-text-muted">
            Brak gotowych ujęć - stwórz avatara i sceny w "Kreatorze avatarów" (pierwszy krok w zasobach produkcyjnych).
          </p>
        ) : (
          <AvatarPicker idPrefix="look-picker" avatars={options} value={value} onChange={onSelect} />
        )}
      </div>
    </div>
  );
}
